import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import SubdomainClaim from "@/components/SubdomainClaim";

export default function AdminSettings() {
  const [tenant, setTenant] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#0F172A");
  const [domains, setDomains] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant").then((r) => {
      setTenant(r.data);
      setName(r.data.name || "");
      setColor(r.data.primary_color || "#0F172A");
      setDomains((r.data.allowed_email_domains || []).join(", "));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/tenant", {
        name,
        primary_color: color,
        allowed_email_domains: domains.split(",").map((d) => d.trim()).filter(Boolean),
      });
      setTenant(r.data);
      toast.success("Workspace updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-settings-page">
      <div>
        <div className="axia-overline">Admin · Settings</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Workspace settings</h1>
        <p className="text-sm text-slate-500 mt-2">Tenant brand, primary color, allowed sign-in domains, and the Axia data principle.</p>
      </div>

      <SubdomainClaim />

      <section className="axia-card p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-400" />
          <h2 className="font-display font-semibold text-slate-900">Brand</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Workspace name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
          </div>
          <div>
            <Label>Primary color</Label>
            <div className="flex gap-2 items-center">
              <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-16 h-10 p-1" data-testid="settings-color" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} className="font-mono text-xs" />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Allowed sign-in domains (comma-separated)</Label>
            <Input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="company.com, company.in" data-testid="settings-domains" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="settings-save">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Save changes
          </Button>
        </div>
      </section>

      <section className="axia-card p-6" data-testid="data-principles">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display font-semibold text-slate-900">Data principle &mdash; what Axia does not store</h2>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-emerald-50/50 border border-emerald-200 rounded-md p-4">
            <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700">We store</div>
            <ul className="mt-2 space-y-1 text-slate-700 text-sm">
              <li>· The complete org chart and reporting lines</li>
              <li>· Every performance cycle, objective, check-in, review, rating</li>
              <li>· The full career timeline of each employee inside your workspace</li>
              <li>· Lifecycle events: joins, promotions, role changes, exits</li>
              <li>· Job grade bands as classification (e.g. <code className="font-mono">M3</code>, <code className="font-mono">L5</code>)</li>
            </ul>
          </div>
          <div className="bg-rose-50/50 border border-rose-200 rounded-md p-4">
            <div className="text-xs uppercase tracking-wide font-semibold text-rose-700">We do not store</div>
            <ul className="mt-2 space-y-1 text-slate-700 text-sm">
              <li>· Salary, gross, net, CTC, TTC, base pay</li>
              <li>· Bonus, commission, equity, ESOP, RSU, stock options</li>
              <li>· Allowances, perks, reimbursements, stipends</li>
              <li>· Any currency-denominated compensation figure</li>
              <li>· Payroll or benefits data</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500 leading-relaxed">
          Compensation lives in your existing payroll / HRIS / comp-management tooling. Axia exposes ratings via export and webhooks so those systems can consume them. CSV imports, custom-field schemas, and integrations all enforce this principle at the boundary.
        </p>
      </section>
    </div>
  );
}
